import { RefObject, useEffect } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal focus handling for side panels and dialogs (ADR-49): on open, focus
 * moves to `initial` (or the first focusable element); Tab stays inside;
 * Escape calls `onEscape`; on close, focus returns to whatever had it before.
 */
export function useModalFocus(
  open: boolean,
  container: RefObject<HTMLElement | null>,
  onEscape: () => void,
  initial?: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const root = container.current;
    const first = initial?.current ?? root?.querySelector<HTMLElement>(FOCUSABLE) ?? root;
    first?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onEscape();
        return;
      }
      if (e.key !== 'Tab' || !root) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };
    root?.addEventListener('keydown', onKey);
    return () => {
      root?.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
    // Re-run only when opening/closing; handlers read the latest props via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
